import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { crmConstant } from "services/Crm/constant";
import { constant } from "common/constant";
import { useEffect, useState } from "react";
import { getApi, putApi } from "common/services/api";
import { toast } from "react-toastify";

const ShareLeadModal = ({
  onClose,
  isOpen,
  leadId,
  moduleId,
  leadName,
  companyName,
  refreshLeads,
  leadOwner,
}) => {
  const user = useSelector((state) => state.user.user);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentOwnerName, setCurrentOwnerName] = useState("");
  const [tempLeadOwner, setTempLeadOwner] = useState(leadOwner || '');

  useEffect(() => {
    if(leadOwner?.length > 0){
      setTempLeadOwner(leadOwner)
    }
  },[leadOwner])

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const result = await getApi("api/user/");
        if (result?.data) {
          const filteredUsers = result?.data?.data.filter(
            (u) => u._id !== user?._id
          );
          setUsers(filteredUsers);
          if (tempLeadOwner.length > 0) {
            setSelectedUserId(tempLeadOwner);
            const currentOwner = result.data?.data.find(
              (u) => u._id === tempLeadOwner
            );
            if (currentOwner) {
              const ownerName =
                currentOwner.firstName && currentOwner.lastName
                  ? `${currentOwner.firstName} ${currentOwner.lastName}`
                  : currentOwner.username;
              setCurrentOwnerName(ownerName);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
        toast.error(crmConstant.someThingWentWrong);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchUsers();
    } else {
      setSelectedUserId("");
      setCurrentOwnerName("");
    }
  }, [isOpen, user?._id, tempLeadOwner]);

  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
  };

  const handleShare = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user to share with");
      return;
    }
    if (selectedUserId === tempLeadOwner) {
      toast.info("This user is already the owner of this lead");
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await putApi(`api/form/edit/${leadId}`, {
        leadOwner: selectedUserId,
        moduleId: moduleId,
      });

      if (response.status === 200) {
        toast.success("Lead shared successfully");
        onClose();
        setTempLeadOwner(selectedUserId)
        // setSelectedUserId(tempLeadOwner)
        if (refreshLeads && typeof refreshLeads === "function") {
          refreshLeads();
        }
      } else {
        toast.error(crmConstant.someThingWentWrong);
      }
    } catch (error) {
      console.error("Failed to share lead:", error);
      toast.error(
        error.response?.data?.message || crmConstant.someThingWentWrong
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal size="md" onClose={onClose} isOpen={isOpen} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader className="convertModalHeader">
          Share Lead
          {leadName && companyName && (
            <Text className="convertHeaderDetail">{`(${leadName} - ${companyName})`}</Text>
          )}
        </ModalHeader>

        <IconButton
          size="xs"
          className="closeIcon"
          onClick={onClose}
          icon={<CloseIcon />}
        />

        <ModalBody className="modalbody">
          <Flex flexDirection="column" gap="16px">
            <Text className="label">
              Select a user to share this lead with:
            </Text>

            {currentOwnerName && (
              <Box mb={2}>
                <Text fontSize="sm" fontWeight="medium">
                  Current owner:{" "}
                  <span className="value">{currentOwnerName}</span>
                </Text>
              </Box>
            )}

            <FormControl>
              <FormLabel className="label">
                User <span className="astrick">*</span>
              </FormLabel>
              <Select
                placeholder="Select user"
                value={selectedUserId}
                onChange={handleUserChange}
                isDisabled={isLoading}
              >
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.username}
                  </option>
                ))}

                {/* Add current user as well if not already in the list */}
                {user && user._id && (
                  <option key={user._id} value={user._id}>
                    {user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName} (You)`
                      : `${user.username} (You)`}
                  </option>
                )}
              </Select>
            </FormControl>

            {isLoading && (
              <Flex justify="center" my={4}>
                <Spinner />
              </Flex>
            )}

            <Box mt={2}>
              <Text className="label" fontSize="sm" color="gray.600">
                Sharing this lead will transfer ownership to the selected user.
              </Text>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button
            sx={{ textTransform: "capitalize" }}
            variant="outline"
            className="cancelBtn theme-btn2"
            size="sm"
            onClick={onClose}
          >
            {constant.cancel}
          </Button>
          <Button
            className="viewEditBtn"
            sx={{ textTransform: "capitalize" }}
            ml={4}
            onClick={handleShare}
            isLoading={isSubmitting}
            isDisabled={
              !selectedUserId || isSubmitting || selectedUserId === tempLeadOwner
            }
          >
            Share
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareLeadModal;
